import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

// Per-user UI preferences saved to the account (follow the practitioner across computers).
let cache = null;
let inflight = null;

function fetchPrefs() {
  if (!inflight) {
    inflight = api.get("/ui-prefs")
      .then((r) => { cache = r.data?.prefs || {}; return cache; })
      .finally(() => { inflight = null; });
  }
  return inflight;
}

function readLegacy(legacyKey) {
  if (!legacyKey) return null;
  try { return localStorage.getItem(legacyKey) || null; } catch { return null; }
}

function savePref(key, v) {
  cache = { ...(cache || {}), [key]: v };
  api.patch("/ui-prefs", { prefs: { [key]: v } }).catch(() => {});
}

// legacyKey: old per-browser localStorage key, copied to the account once when the account has no value yet.
export function useUiPref(key, fallback, legacyKey) {
  const [value, setValue] = useState(() => {
    if (cache && key in cache) return cache[key];
    return readLegacy(legacyKey) ?? fallback;
  });
  const valueRef = useRef(value);
  valueRef.current = value;
  const fallbackRef = useRef(fallback);

  useEffect(() => {
    let live = true;
    fetchPrefs()
      .then((prefs) => {
        if (!live) return;
        if (key in prefs) { setValue(prefs[key]); return; }
        const legacy = readLegacy(legacyKey);
        if (legacy != null) { setValue(legacy); savePref(key, legacy); }
        else setValue(fallbackRef.current); // don't keep a previous user's cached value
      })
      .catch(() => {});
    return () => { live = false; };
  }, [key, legacyKey]);

  const update = useCallback((next) => {
    const v = typeof next === "function" ? next(valueRef.current) : next;
    valueRef.current = v;
    setValue(v);
    savePref(key, v);
  }, [key]);

  return [value, update];
}
